import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { ApiService } from './api.service';
import { map } from 'rxjs/operators';

interface AssessmentStep {
  id: string;
  type: 'canvas' | 'trl' | 'brief' | 'sustainability';
  name: string;
  readingTimeMin: number;
  totalPoints: number;
  completedPoints: number;
  isCompleted: boolean;
  weight: number;
}

@Injectable({ providedIn: 'root' })
export class ProgressTrackerService {
  private steps = new Map<string, AssessmentStep>();
  private startTime = Date.now();

  private progressPercent$ = new BehaviorSubject<number>(0);
  private estimatedTimeLeft$ = new BehaviorSubject<number>(0);
  private elapsedTime$ = new BehaviorSubject<number>(0);

  constructor(private api: ApiService) {
    this.api.progressUpdated$.subscribe(async() => {      
      const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
      await sleep(1000);
      this.initialize();
    });
  }

  ngOnInit(): void {    
    this.initialize();
  }

  /**
   * Initializes steps for the active assessment.
   * Should be called once after switching/loading an assessment.
   */
  public initialize(): void {
    console.log('ProgressTrackerService initialized.');
    this.reset();
  
    this.api.getAssessment().subscribe((assessment: any) => {
      forkJoin({
        questionnaires: this.api.getAssessmentQuestionnaires(),
        sustainability: this.api.getUserIndicators(assessment.id)
      }).subscribe(({ questionnaires, sustainability }) => {
  
        const completedPoints: Record<string, number> = {
          canvas: 0,
          trl: 0,
          brief: 0,
          sustainability: 0
        };
  
        // Canvas / TRL / Brief questionnaire counting
        const templates = {
          canvas: 'CANVAS',
          trl: 'TRL',
          brief: 'BRIEF'
        };
  
        Object.entries(templates).forEach(([stepId, templateId]) => {
          const section = questionnaires.find((s: any) =>
            s.template?.name?.toLowerCase().trim().includes(templateId.toLowerCase().trim())
          );
  
          if (!section) {
            console.warn(`No matching section found for step: ${stepId}`);
            return;
          }
  
          const answers = section.template.answers || [];
          const questions = section.template.questions || [];
  
          const enriched = questions.map((q: any) => {
            const matchedAnswer = answers.find((a: any) => a.questionId === q.id);
            return { ...q, answer: matchedAnswer };
          });
  
          const count = enriched.filter(s =>
            stepId === 'trl'
              ? ['yes', 'no'].includes(s.answer?.answerText?.toLowerCase())
              : !!s.answer?.answerText?.trim()
          ).length;
  
          completedPoints[stepId] = count;
        });
  
        // Sustainability indicators
        const indicators = this.structureIndicatorsByNode(sustainability);
        const sustainabilityAnswers = this.countAnsweredSustainabilityIndicators(indicators);
        completedPoints.sustainability = sustainabilityAnswers;
        
        // Weight in % of time estimated for each step; Estimation from Tanja Dergan.
        const stepConfigs: Omit<AssessmentStep, 'completedPoints' | 'isCompleted'>[] = [
          { id: 'canvas', type: 'canvas', name: 'Business Model Canvas', readingTimeMin: 0, totalPoints: 9, weight: 30 },
          { id: 'trl', type: 'trl', name: 'Technology Readiness', readingTimeMin: 0, totalPoints: 9, weight: 5 },
          { id: 'brief', type: 'brief', name: 'Business Model', readingTimeMin: 0, totalPoints: 11, weight: 40 },
          {
            id: 'sustainability',
            type: 'sustainability',
            name: 'Sustainability Assessment',
            readingTimeMin: 0,
            totalPoints: this.getSustainabilityTotalPoints(assessment.nodes),
            weight: 25
          }
        ];        
  
        stepConfigs.forEach(step => {
          this.steps.set(step.id, {
            ...step,
            completedPoints: completedPoints[step.id] || 0,
            isCompleted: false
          });
        });
  
        this.updateProgress();
      });
    });
  }

  private structureIndicatorsByNode(inputs: any[]): Record<string, Record<string, any[]>> {
    const indicators = {};
    for (let input of inputs) {
      if (!indicators[input.pillarId]) {
        indicators[input.pillarId] = {};
      }
      if (!indicators[input.pillarId][input.nodeId]) {
        indicators[input.pillarId][input.nodeId] = [];
      }
      indicators[input.pillarId][input.nodeId].push(input);
    }
    return indicators;
  }

  countAnsweredSustainabilityIndicators(data: any): number {
    let count = 0;  
    for (const pillarId of Object.keys(data)) {
      const nodes = data[pillarId];
      for (const nodeId of Object.keys(nodes)) {
        const indicators = nodes[nodeId];
        for (const indicator of indicators) {
          if (indicator.Assessment_indicator_answers?.length > 0) {
            count++;
          }
        }
      }
    }  
    return count;
  }

  getSustainabilityTotalPoints(nodes: { key: string; enabled: boolean }[]): number {
    // TODO do actual counting from data
    const nodePointsMap: Record<string, number> = {
      RadiantProduction: 45,
      RadiantProcessing: 44,
      RadiantTransportAndDistribution: 19,
      RadiantMarketAndRetailers: 42
    };
  
    return nodes
      .filter(node => node.enabled)
      .reduce((sum, node) => sum + (nodePointsMap[node.key] || 0), 0);
  }

  /**
   * Resets internal state (without clearing observable history).
   */
  public reset(): void {
    this.steps.clear();
    this.startTime = Date.now();
    this.updateProgress();
  }

  /**
   * Called whenever an update in step progress occurs.
   */
  private updateProgress() {
    const all = Array.from(this.steps.values());

    const percent = all.reduce((sum, step) => {
      const ratio = step.totalPoints > 0 ? step.completedPoints / step.totalPoints : 0;
      return sum + ratio * step.weight;
    }, 0);

    this.progressPercent$.next(Math.round(percent));

    const totalWeightedPoints = all.reduce((sum, step) => sum + step.totalPoints * step.weight, 0);
    const completedWeightedPoints = all.reduce((sum, step) => sum + step.completedPoints * step.weight, 0);
    const estimatedRemaining = totalWeightedPoints - completedWeightedPoints;
    const timePerPoint = 2;

    this.estimatedTimeLeft$.next(Math.ceil(estimatedRemaining / 100 * timePerPoint)); // scale to 100%
  }

  // Getters for UI binding
  getProgressPercent$() {
    return this.progressPercent$.asObservable();
  }

  getEstimatedTimeLeft$() {
    return this.estimatedTimeLeft$.asObservable();
  }

  getElapsedTime$() {
    return this.elapsedTime$.asObservable();
  }
}
